import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import {
  copyToClipboard,
  DCEvent,
  EventDispatcher,
  confirmAction,
  httpGet,
  httpPost,
  navigate,
  showGeneralError,
  showGeneralSuccess,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { BR_STATUS_LABEL, PendingDecisionListItem } from "@/AdminApp/AdminAppController";
import { TabButtonProps } from "@/components-v2/Tabs";
import { TMessageKey } from "@/i18n/types.gen";
import { BR_DECISION_STATUS, BR_STATUS, CompanyItem, DecisionStatus } from "@/common/common";
import { DocumentSigningStatus } from "@/document/documentApi";
import { DocumentType } from "@/common/documents";
import { CustomDocumentType } from "@/dbot/customDocument/customDocumentDomain";
import {
  compact,
  groupBy,
  mapValues,
  maxBy,
  minBy,
  now,
  orderBy,
  sortBy,
  uniq,
  uniqBy,
} from "lodash";
import { isBlank } from "@/utils/strings";
import { FilePreviewProps } from "@/components-v2/FilePreview";
import { DecisionType } from "@/common/decisions";
import {
  AdditionalBRSigner,
  AdditionalBRSignerReason,
  AttachmentType,
  BRSigner,
  BRSignerRole,
  getDecisionName,
} from "@/dbot/common/commonDomain";
import { formatDate } from "@/i18n/dateFormat";
import { Language } from "@/i18n/languages";
import {
  CompaniesForPreview,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import {
  combineDateAndTime,
  diffDays,
  plusDays,
  toDate,
  toLocalDateISOString,
} from "@/utils/Dates";
import { AltinnSigner, EventLog } from "@/common/eventLogs";
import { TMessage } from "@/i18n/tt";
import { PersonalDeputyFor } from "@/dbot/ElectionOfCompanyBoard/electionOfCompanyBoardDomain";
import { RoleType } from "@/types/enums/RoleType";
import { getFullName, sortedRoleTypes } from "@/utils/Roles";
import { newUuid } from "@/utils/uuid";
import { ContributionType } from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalDomain";
import { formatOrgNum } from "@/utils/other";
import {
  MergerDecisionType,
  MergerInvoiceRecipient,
  SubsidiaryMergerCompanyRole,
} from "@/dbot/merger/mergerDomain";
import { FgColor } from "@/components-v2/colors";
import { IconName } from "@/components-v2/iconMapping";
import { ShareCapitalDuration } from "@/dbot/increaseShareCapitalPowerOfAttorney/increaseShareCapitalPowerOfAttorneyDomain";
import { User } from "@/types/UserInfo";
import { when } from "@/utils/functions";
import { next, prev, single } from "@/utils/arrays";

export const initFollowUpsPage = (decisionId?: string): DCEvent[] => {
  return [
    setInFollowUpsState("isLoading", true),
    setInFollowUpsState("selectedDecision", undefined),
    decisionId === undefined
      ? getPendingDecisions()
      : selectDecision(decisionId, (r) => [
          setInFollowUpsState("selectedCompany", r.decision.company),
        ]),
  ];
};

export const getPendingDecisions = (): DCEvent =>
  followUpsApi.getPendingDecisions({
    onSuccess: (decisions) =>
      compact([
        setInFollowUpsState("decisions", decisions.pendingDecisions),
        setInFollowUpsState("responsibles", decisions.responsibles),
        setInFollowUpsState("isLoading", false),
      ]),
    onFailure: () => [showGeneralError()],
  });

export type FollowUpTab =
  | "new"
  | "deleted"
  | "sent-for-signing"
  | "processing"
  | "approved"
  | "rejected"
  | "needs-documentation";

const followUpTabs: { [key in FollowUpTab]: TMessageKey } = {
  new: "followup.tabs.new",
  "sent-for-signing": "followup.tabs.sent-for-signing",
  processing: "followup.tabs.processing",
  approved: "followup.tabs.approved",
  rejected: "followup.tabs.rejected",
  "needs-documentation": "followup.tabs.needs-documentation",
  deleted: "followup.tabs.deleted",
} as const;

export type FollowUpsViewProps = {
  decisions: PendingDecisionProps[];
  tabs: TabButtonProps[];
  activeTab: FollowUpTab;
  onSelectDecision: (decision: PendingDecisionListItem) => void;
  selectedDecision?: DecisionDetailViewProps;
  filterText: string;
  filterCompaniesModel: RadioFieldModel<CompanyItem>;
  filterResponsibleModel: RadioFieldModel<Responsible>;
  updateFilterText: (s: string) => void;
};

export type PendingDecisionProps = {
  responsibleModel: RadioFieldModel<Responsible>;
} & PendingDecisionListItem;

export type PendingDecisionGroup = {
  companyName: string;
  orgNo: string;
  submissionReference?: number;
  count: number;
  status?: BR_STATUS;
  sentToBRTime?: string;
  signerReminderDate?: string;
  updateTime?: string;
  responsible: Responsible | undefined | "mixed";
};

export function pendingDecisionGroup(decisions: PendingDecisionProps[]): PendingDecisionGroup {
  const responsibleIds = uniq(decisions.map((d) => d.responsibleId));
  return {
    companyName: decisions[0].companyName,
    orgNo: decisions[0].orgNo,
    submissionReference: decisions[0].submissionReference,
    count: decisions.length,
    status: single(uniq(decisions.map((d) => d.status))),
    sentToBRTime: minBy(decisions, (d) => d.sentToBRTime)?.sentToBRTime,
    signerReminderDate: maxBy(decisions, (d) => d.signerReminderDate)?.signerReminderDate,
    updateTime: maxBy(decisions, (d) => d.updateTime)?.updateTime,
    responsible: responsibleIds.length === 1 ? decisions[0].responsibleModel.value : "mixed",
  };
}

export const pendingDecisionGroupsByReference = (
  decisions: PendingDecisionProps[],
): Record<string, PendingDecisionGroup> =>
  mapValues(
    groupBy(
      decisions.filter((d) => d.submissionReference !== undefined),
      (d) => d.submissionReference,
    ),
    pendingDecisionGroup,
  );

export type DecisionDetailViewProps = {
  decision: PendingDecisionDetails;
  companies: CompaniesForPreview;
  documents: DocumentMetadata[];
  projectDecisions: PendingProjectDecision[];
  projectDecisionsByCompany: PendingProjectDecision[];
  submissionDecisions: PendingProjectDecision[];
  onGenerateNotificationEmail: () => void;
  onMarkSubmissionSentForSigning: () => void;
  onMarkDecisionSentForSigning: (decisionId: string) => void;
  showAltinnSubmission: boolean;
  goToAltinnSubmission: () => void;
  onCloseAltinnSubmission: () => void;
  previewDocument?: FilePreviewProps;
  brPublishingDateModel: LocalDateFieldModel;
  isRegisteredOnDcompany: boolean;
  responsibles: Responsible[];
  assignedToModel: RadioFieldModel<Responsible>;
  onChangeResponsible: (decisionIds: string[], responsible?: Responsible) => void;
  onBack: () => void;
  openSignerReminderDialog: () => void;
  openAddToSubmissionReferenceDialog: () => void;
  onResetSubmission: () => void;
  openUpdateBRStatusDialog: (selectedCompany?: CompanyItem) => void;
  onUpdateStatus: (brPublishingDate: LocalDateFieldModel, status?: BR_STATUS) => void;
  onToggleDocumentPreview: (documentId?: string) => void;
  onToggleAttachmentPreview: (id?: string) => void;
  onToggleDecisionPreview: (decision: { decisionType: DecisionType; changedData: string }) => void;
};

export const unassigned: Responsible = {
  id: "unassigned",
  name: "Unassigned",
  email: "",
};

export type GetPendingDecisionsResponse = {
  pendingDecisions: PendingDecisionListItem[];
  responsibles: Responsible[];
};

export type Responsible = {
  id: string;
  name: string;
  email: string;
};

export type FollowUpDescription = {
  type: string;
  title?: string;
  value?: string;
};

export type GetPendingDecisionDetailsResponse = {
  decision: PendingDecisionDetails;
  responsibles: Responsible[];
  projectDecisions: PendingProjectDecision[];
};

export type PendingDecisionDetails = {
  decisionId: string;
  projectId: string;
  company: CompanyItem;
  status: BR_STATUS;
  sentToBRTime: string;
  sentForSigningTime?: string;
  sentForSigningBy?: string;
  brPublishingDate?: string;
  decisionType: DecisionType;
  submittedByName?: string;
  submittedByEmail?: string;
  decisionStatus: DecisionStatus;
  documents: DocumentMetadata[];
  attachments: UploadedAttachment[];
  projectName: string;
  brSigners: BRSigner[];
  eventLogs: EventLog[];
  followUpDescriptions: FollowUpDescription[];
  changedData: string;
  altinnSubmissionData?: AltinnSubmissionData;
  responsibleId?: string;
  isRegistration: boolean;
  mergerCounterpart?: CompanyItem;
};

export type AltinnSubmissionContact = {
  name?: string;
  orgNo?: string;
  email?: string;
};

export type AttachmentConfirmer = {
  attachmentType: AttachmentType;
  contact: AltinnSubmissionContact;
};

export type PendingProjectDecision = {
  decisionId: string;
  company: CompanyItem;
  decisionType: DecisionType;
  projectId: string;
  status: BR_DECISION_STATUS;
  changedData: string;
  altinnSubmissionData?: AltinnSubmissionData;
  additionalSigners: AdditionalBRSigner[];
  sentToBRTime: string;
  submittedByName?: string;
  submittedByEmail?: string;
  brSigners: BRSigner[];
  responsibleId?: string;
  isRegistration: boolean;
  submissionReference?: number;
  notificationEmail?: string;
  requiredDocumentIds: string[];
  attachmentIds: string[];
};

export type AltinnSubmissionData =
  | ElectionOfCompanyBoardAltinnSubmission
  | IncreaseOfShareCapitalAltinnSubmission
  | ChangeOfAuditorAltinnSubmission
  | ChangeOfAddressAltinnSubmission
  | CompanyNameChangeAltinnSubmission
  | ChangeArticlesOfAssociationAltinnSubmission
  | EmploymentOfManagingDirectorAltinnSubmission
  | IncreaseShareCapitalPowerOfAttorneyAltinnSubmission
  | MergerAltinnSubmission;

export type MergerAltinnSubmission = {
  type: DecisionType.merger;
  decisionDate?: string;
  mergerType?: MergerDecisionType;
  companies: { company: { name: string; orgNo: string }; role: SubsidiaryMergerCompanyRole }[];
  invoiceRecipient?: MergerInvoiceRecipient;
  invoiceRecipientCompany?: { name: string; orgNo: string };
};

export const mergerRoleLabel: { [key in SubsidiaryMergerCompanyRole]: TMessageKey } = {
  ACQUIRING: "followup.decision.altinn.merger.role.acquiring",
  TRANSFERRING: "followup.decision.altinn.merger.role.transferring",
} as const;

export const mergerTypeLabel: { [key in MergerDecisionType]: TMessageKey } = {
  mergerBetweenParentAndWhollyOwnedSubsidiary:
    "followup.decision.altinn.merger.type.mergerBetweenParentAndWhollyOwnedSubsidiary",
  mergerBetweenWhollyOwnedSubsidiaries:
    "followup.decision.altinn.merger.type.mergerBetweenWhollyOwnedSubsidiaries",
} as const;

type ArticlesOfAssociationDecisionType =
  | DecisionType.changeArticlesOfAssociation
  | DecisionType.removeCompanyBoardFromArticlesOfAssociation
  | DecisionType.removeSignatureFromArticlesOfAssociation
  | DecisionType.insertCompanyBoardInArticlesOfAssociation
  | DecisionType.insertSignatureInArticlesOfAssociation
  | DecisionType.changeCompanyBoardInArticlesOfAssociation
  | DecisionType.changeSignatureInArticlesOfAssociation;

export type ChangeArticlesOfAssociationAltinnSubmission = {
  type: ArticlesOfAssociationDecisionType;
  articlesOfAssociationDate?: string;
};

export type CompanyNameChangeAltinnSubmission = {
  type: DecisionType.companyName;
  articlesOfAssociationDate?: string;
  name?: string;
};

export type ChangeOfAuditorAltinnSubmission = {
  type: DecisionType.auditor;
  name: string;
  email: string;
  organisationNumber: string;
};

export type ChangeOfAddressAltinnSubmission = {
  type: DecisionType.changeOfAddress;
  options: ChangeOfAddressOption[];
  addressLine1?: string;
  addressLine2?: string;
  zip?: string;
  city?: string;
  country?: string;
  mailingAddressLine1?: string;
  mailingAddressLine2?: string;
  mailingZip?: string;
  mailingCity?: string;
  mailingCountry?: string;
};

export type ChangeOfAddressOption =
  | "BUSINESS_ADDRESS"
  | "MAILING_ADDRESS"
  | "REMOVE_MAILING_ADDRESS";

export type IncreaseOfShareCapitalAltinnSubmission = {
  type: DecisionType.increaseOfShareCapital;
  articlesOfAssociationDate?: string;
  usePowerOfAttorney: boolean;
  isShareIssueInterval: boolean;
  shareCapitalIncrease: number;
  contributionTypes: ContributionType[];
  newShareCapital: number;
  newNumberOfShares: number;
  newShareNominalValue: number;
  requiredAttachments: AttachmentType[];
  attachmentConfirmer?: AttachmentConfirmer;
};

export type ElectionOfCompanyBoardAltinnSubmission = {
  type: DecisionType.electionOfCompanyBoard;
  newCompanyBoard: AltinnSubmissionCompanyRole[];
  genderCompositionRequirement: boolean;
};

export type EmploymentOfManagingDirectorAltinnSubmission = {
  type: DecisionType.employmentOfManagingDirector;
  name: string;
  identityNumber: string;
  email?: string;
};

export enum BoardMemberChangeType {
  NEW = "NEW",
  UPDATED = "UPDATED",
  REMOVED = "REMOVED",
}

export type AltinnSubmissionCompanyRole = {
  id: string;
  identityNumber?: string;
  role: RoleType;
  name: string;
  email?: string;
  personalDeputyFor?: PersonalDeputyFor[];
  changeType?: BoardMemberChangeType;
};

export type AltinnSubmissionSigner = {
  id: string;
  name?: string;
  copyName?: string;
  email?: string;
  description?: TMessage;
};

export type AltinnSignerReminderProps = {
  decisionId: string;
  decisions: OptionFieldModel<PendingProjectDecision>;
  signers: OptionFieldModel<AltinnSubmissionSigner>;
  subject: StringFieldModel;
  body: StringFieldModel;
};

export type DecisionBRStatusUpdateProps = {
  decisionId?: string;
  decisions: OptionFieldModel<PendingProjectDecision>;
  body: StringFieldModel;
};

export type CreateSubmissionReferenceResponse = {
  submissionReference: number;
  notificationEmail: string;
};

export type Submission = {
  reference: number;
  decisions: PendingDecisionListItem[];
  statuses: TMessage[];
};

export type AddToSubmissionReferenceProps = {
  decisionId: string;
  submissions: RadioFieldModel<Submission | null>;
};

const isActiveSubmissionStatus = (status: BR_STATUS) =>
  status !== "BR_APPROVED" && status !== "DELETED";

export function activeSubmissionsForCompany(
  decisionId: string,
  orgNo: string,
  decisions: PendingDecisionListItem[],
): Submission[] {
  const ownReference = decisions.find((d) => d.id === decisionId)?.submissionReference;
  const allCompanyDecisions = decisions.filter((d) => d.orgNo === orgNo);
  return uniq(compact(allCompanyDecisions.map((d) => d.submissionReference)))
    .filter((reference) => reference !== ownReference)
    .map((reference) => {
      const submissionDecisions = allCompanyDecisions.filter(
        (d) => d.submissionReference === reference,
      );
      return {
        reference,
        decisions: submissionDecisions,
        statuses: uniq(submissionDecisions.map((d) => d.status)).map((status) => ({
          key: BR_STATUS_LABEL[status].label,
        })),
      };
    })
    .filter((submission) => submission.decisions.some((d) => isActiveSubmissionStatus(d.status)));
}

const listDecisionName = (decision: PendingDecisionListItem): TMessage =>
  decision.isRegistration
    ? {
        key: "followup.decision.registration",
        params: { decision: getDecisionName(decision.decisionType) },
      }
    : getDecisionName(decision.decisionType);

const showAddToSubmissionReferenceDialog = (decision: PendingDecisionDetails): DCEvent =>
  followUpsApi.getPendingDecisions({
    onSuccess: (response) => [
      setInFollowUpsState("decisions", response.pendingDecisions),
      {
        type: "show-dialog",
        dialog: {
          type: "add-to-submission-reference",
          props: {
            decisionId: decision.decisionId,
            submissions: new RadioFieldModel<Submission | null>({
              value: null,
              options: activeSubmissionsForCompany(
                decision.decisionId,
                decision.company.organisationNumber,
                response.pendingDecisions,
              ),
              idFn: (submission) => String(submission?.reference ?? ""),
              labelFn: (submission) =>
                submission
                  ? {
                      key: "followup.decision.add-to-submission-reference.option",
                      params: {
                        reference: submission.reference,
                        decisions: {
                          type: "list",
                          value: submission.decisions.map(listDecisionName),
                        },
                      },
                    }
                  : { translated: "" },
              required: true,
            }),
          },
        },
      },
    ],
    onFailure: () => [showGeneralError()],
  });

export const addToSubmissionReference = (decisionId: string, reference: number): DCEvent[] => [
  followUpsApi.addToSubmissionReference({
    reference,
    decisionId,
    onSuccess: () => [
      { type: "closeDialog" },
      showGeneralSuccess(),
      selectDecision(decisionId),
      getPendingDecisions(),
    ],
    onFailure: () => [showGeneralError()],
  }),
];

export type CreateSubmissionReferenceProps = {
  decisionId: string;
  decisions: OptionFieldModel<PendingProjectDecision>;
};

export type IncreaseShareCapitalPowerOfAttorneyAltinnSubmission = {
  type: DecisionType.increaseShareCapitalPowerOfAttorney;
  poaStart: string;
  poaEnd?: string;
  shareCapitalIncrease: number;
  shareCapitalDuration: ShareCapitalDuration;
};

export function sortedAltinnRoles(roles: AltinnSubmissionCompanyRole[]) {
  return sortBy(
    roles,
    (r) => (r.changeType === BoardMemberChangeType.REMOVED ? 1 : 0),
    (r) => sortedRoleTypes.indexOf(r.role),
    (r) => r.name,
  );
}

export function getProjectDecisionsNotSentToBR(projectDecisions: PendingProjectDecision[]) {
  return projectDecisions.filter((a) => a.status === "NOT_SENT");
}

export function getProjectDecisionsForAltinnSubmission(projectDecisions: PendingProjectDecision[]) {
  return projectDecisions.filter((a) => a.status !== "NOT_SENT");
}

// Submissions predating the reference have none, and their grouping is unknown.
export function getSubmissionDecisions(
  projectDecisionsByCompany: PendingProjectDecision[],
  mainDecisionId: string,
): PendingProjectDecision[] {
  const decisions = getProjectDecisionsForAltinnSubmission(projectDecisionsByCompany);
  const mainDecision = decisions.find((d) => d.decisionId === mainDecisionId);
  const reference =
    mainDecision === undefined
      ? decisions.find((d) => d.submissionReference !== undefined)?.submissionReference
      : mainDecision.submissionReference;
  return reference === undefined
    ? compact([mainDecision])
    : decisions.filter((d) => d.submissionReference === reference);
}

// Altinn's own attachment types, Norwegian only.
const altinnDocumentLabels: Partial<
  Record<AttachmentType | CustomDocumentType | DocumentType, string>
> = {
  [DocumentType.general_meeting]: "Protokoller fra generalforsamling",
  [DocumentType.board_meeting]: "Protokoller fra styremøte",
  [DocumentType.articlesofassociation]: "Vedtekter",
  CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS: "Erklæringer fra revisor",
  CONFIRMATION_SHARE_CAPITAL_AUDITOR: "Redegjørelse med bekreftelse av revisor",
  CONFIRMATION_SHARE_CAPITAL_DEBT_CONTRIBUTION: "Redegjørelse med bekreftelse av revisor (gjeld)",
  boardStatementDebtConversion: "Redegjørelse med bekreftelse av revisor (gjeld)",
  CONFIRMATION_SHARE_CAPITAL_KIND_CONTRIBUTION:
    "Redegjørelse med bekreftelse av revisor (tingsinnskudd)",
  boardStatementContributionInKind: "Redegjørelse med bekreftelse av revisor (tingsinnskudd)",
};

const altinnSubmissionDocumentOrder = Object.values(altinnDocumentLabels);

export type AltinnSubmissionDocument = {
  id: string;
  name: string;
  altinnLabel?: string;
  isAttachment: boolean;
};

export function getAltinnSubmissionDocuments(
  documents: DocumentMetadata[],
  attachments: UploadedAttachment[],
  decisions: PendingProjectDecision[],
): AltinnSubmissionDocument[] {
  const required = [
    ...documents.map((doc) => ({
      id: doc.documentId,
      name: doc.title,
      altinnLabel: altinnDocumentLabels[doc.customDocumentType ?? doc.documentType],
      isAttachment: false,
    })),
    ...attachments.map((attachment) => ({
      id: attachment.id,
      name: attachment.filename,
      altinnLabel: attachment.type && altinnDocumentLabels[attachment.type],
      isAttachment: true,
    })),
  ];
  return sortBy(
    required.filter((doc) => getDecisionsRequiringDocument(doc, decisions).length > 0),
    (doc) =>
      doc.altinnLabel === undefined
        ? Infinity
        : altinnSubmissionDocumentOrder.indexOf(doc.altinnLabel),
  );
}

export function getCompanyDocuments(
  decision: PendingDecisionDetails,
  orgNo: string | undefined,
): DocumentMetadata[] {
  return decision.documents.filter(
    (d) =>
      d.orgNo === orgNo ||
      (orgNo === decision.company.organisationNumber &&
        d.orgNo === decision.mergerCounterpart?.organisationNumber),
  );
}

export function getDecisionsRequiringDocument(
  document: AltinnSubmissionDocument,
  decisions: PendingProjectDecision[],
): PendingProjectDecision[] {
  return decisions.filter((d) =>
    document.isAttachment
      ? d.attachmentIds.includes(document.id)
      : d.requiredDocumentIds.includes(document.id),
  );
}

export const decisionNameWithReference = (decision: PendingProjectDecision): TMessage =>
  decision.submissionReference === undefined
    ? getDecisionName(decision.decisionType)
    : {
        key: "followup.decision.altinn.decision-with-reference",
        params: {
          decisionName: getDecisionName(decision.decisionType),
          reference: decision.submissionReference,
        },
      };

export function getProjectDecisionsByCompany(
  projectDecisions: PendingProjectDecision[],
  selectedCompany?: CompanyItem,
): PendingProjectDecision[] {
  return projectDecisions.filter(
    (d) => selectedCompany?.organisationNumber === d.company.organisationNumber,
  );
}

export function getProjectDecisionCompaniesSelectorProps(
  dispatch: EventDispatcher,
  projectDecisions: PendingProjectDecision[],
  selectedCompany?: CompanyItem,
): CompaniesForPreview {
  const sortedProjectDecisionsForSubmission = orderBy(
    getProjectDecisionsForAltinnSubmission(projectDecisions),
    "sentToBRTime",
    "desc",
  );
  const companies = sortedProjectDecisionsForSubmission
    .flatMap((decision) => decision.company)
    .filter(
      (item, index, self) =>
        index ===
        self.findIndex((company) => company.organisationNumber === item.organisationNumber),
    );
  return {
    companies: companies,
    selectedCompany: selectedCompany,
    selectCompany: (company) => dispatch([setInFollowUpsState("selectedCompany", company)]),
    previousCompany: when(
      prev(
        companies,
        (company) => company.organisationNumber === selectedCompany?.organisationNumber,
      ),
      (company) => () => dispatch([setInFollowUpsState("selectedCompany", company)]),
    ),
    nextCompany: when(
      next(
        companies,
        (company) => company.organisationNumber === selectedCompany?.organisationNumber,
      ),
      (company) => () => dispatch([setInFollowUpsState("selectedCompany", company)]),
    ),
  };
}

export const brSignerPersonKey = (signer: BRSigner): string =>
  `${signer.role}:${signer.name}:${signer.email ?? ""}`;

export function getAltinnSubmissionBRSigners(
  projectDecisions: PendingProjectDecision[],
): BRSigner[] {
  return getProjectDecisionsForAltinnSubmission(projectDecisions)
    .flatMap((decision) => decision.brSigners)
    .filter(
      (item, index, self) =>
        index === self.findIndex((s) => brSignerPersonKey(s) === brSignerPersonKey(item)),
    );
}

export function getAllSubmittedBy(projectDecisions: PendingProjectDecision[]): string[] {
  return getProjectDecisionsForAltinnSubmission(projectDecisions)
    .filter(
      (item, index, self) =>
        index === self.findIndex((decision) => decision.submittedByEmail === item.submittedByEmail),
    )
    .map((decision) => `${decision.submittedByName} (${decision.submittedByEmail})`);
}

export function getAltinnSubmissionDecisionSigners(
  projectDecisions: PendingProjectDecision[],
): AltinnSubmissionSigner[] {
  return getProjectDecisionsForAltinnSubmission(projectDecisions)?.flatMap<AltinnSubmissionSigner>(
    (decision) => getDecisionSigners(decision.additionalSigners),
  );
}

export function getDecisionsAwaitingSigning(
  projectDecisions: PendingProjectDecision[],
  company: CompanyItem,
): PendingProjectDecision[] {
  return getProjectDecisionsByCompany(projectDecisions, company).filter(
    (d) => d.status === "SENT_FOR_SIGNING",
  );
}

export function getAllAltinnSubmissionSigners(
  projectDecisions: PendingProjectDecision[],
): AltinnSubmissionSigner[] {
  return [
    ...getAltinnSubmissionBRSigners(projectDecisions).map((signer) => ({
      id: newUuid(),
      name: signer.name,
      email: signer.email,
      description: brSignerRoleLabel[signer.role],
    })),
    ...getAltinnSubmissionDecisionSigners(projectDecisions),
  ];
}

export function getDecisionSignersList(
  decision: PendingDecisionDetails,
  projectDecisions: PendingProjectDecision[],
): TMessage[] {
  const decisions = getDecisionsAwaitingSigning(projectDecisions, decision.company);
  return getAllAltinnSubmissionSigners(decisions).map<TMessage>((signer) => ({
    key: "followup.decision.altinn.signer.label",
    params: {
      description: signer.description
        ? { type: "uncapitilize", value: signer.description }
        : undefined,
      name: signer.name,
      email: signer.email,
    },
  }));
}

function additionalBRSignerDescription(reason: AdditionalBRSignerReason): TMessage {
  switch (reason) {
    case AdditionalBRSignerReason.NEW_TO_BOARD:
      return { key: "followup.decision.altinn.election-of-company-board.new-board-member" };
    case AdditionalBRSignerReason.BOARD_ROLE_CHANGE:
      return { key: "followup.decision.altinn.election-of-company-board.updated-board-member" };
    case AdditionalBRSignerReason.NEW_MANAGING_DIRECTOR:
      return { key: "followup.decision.altinn.new-managing-director" };
    case AdditionalBRSignerReason.NEW_AUDITOR:
      return { key: "followup.decision.altinn.auditor.new" };
    case AdditionalBRSignerReason.AUDITOR:
      return { key: "followup.decision.altinn.auditor" };
    case AdditionalBRSignerReason.CONFIRMS_SHARE_CAPITAL_CONTRIBUTIONS:
      return { key: "dbot.requiredAttachments.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS" };
  }
}

export function getDecisionSigners(
  additionalSigners: AdditionalBRSigner[],
): AltinnSubmissionSigner[] {
  return additionalSigners.map((signer) => ({
    id: newUuid(),
    name:
      signer.name && signer.organisationNumber
        ? `${signer.name} (${formatOrgNum(signer.organisationNumber)})`
        : signer.name,
    copyName: signer.organisationNumber,
    email: signer.email,
    description: additionalBRSignerDescription(signer.reason),
  }));
}

export function getSortedBRStatusUpdateEmails(eventLogs: EventLog[]) {
  const sentForSigningReminders = eventLogs.filter(
    (log) => log.eventType === "BRStatusUpdateEmail",
  );
  return sortBy(sentForSigningReminders, (event) => event.timestamp);
}

export function getSortedSignerReminders(eventLogs: EventLog[]) {
  const sentForSigningReminders = eventLogs.filter(
    (log) => log.eventType === "BRSignerReminderEmail",
  );
  return sortBy(sentForSigningReminders, (event) => event.timestamp);
}

export function getSentForSigningTime(decision: PendingDecisionDetails) {
  const sortedStatusEmails = getSortedBRStatusUpdateEmails(decision.eventLogs);
  return decision.sentForSigningTime ?? sortedStatusEmails[-1]?.timestamp ?? decision.sentToBRTime;
}

export function getNextReminderTime(decision: PendingDecisionDetails) {
  const sortedReminders = getSortedSignerReminders(decision.eventLogs);
  return plusDays(
    sortedReminders[-1]?.timestamp ?? getSentForSigningTime(decision),
    7,
  ).toISOString();
}

export type DocumentMetadata = {
  documentId: string;
  documentType: DocumentType;
  customDocumentType?: CustomDocumentType;
  orgNo: string;
  title: string;
  signingStatus: DocumentSigningStatus;
  requiredForSubmission: boolean;
};

function prepTabs(decisions: PendingDecisionListItem[], activeTab: FollowUpTab): TabButtonProps[] {
  return Object.entries(followUpTabs).map(([id, label]) => ({
    id,
    label: { key: label, params: { count: countForTab(id as FollowUpTab, decisions) } },
    isActive: activeTab === id,
    onSelect: [setInFollowUpsState("activeTab", id as FollowUpTab)],
  }));
}

export const allCompanies: CompanyItem = { organisationNumber: "all", companyName: "" };
export const allResponsibles: Responsible = { id: "all", name: "", email: "" };

function createFilterCompaniesModel(
  dispatch: EventDispatcher,
  value: CompanyItem,
  decisions: PendingDecisionListItem[],
) {
  const options = decisions.reduce<CompanyItem[]>(
    (acc, { orgNo, companyName }) => {
      if (!acc.some((item) => item.organisationNumber === orgNo)) {
        acc.push({
          organisationNumber: orgNo,
          companyName: companyName,
        });
      }
      return acc;
    },
    [allCompanies],
  );

  return new RadioFieldModel({
    value: value,
    labelFn: (option) =>
      option.organisationNumber === allCompanies.organisationNumber
        ? { key: "companies-all" }
        : { translated: option.companyName },
    idFn: (option) => option.organisationNumber,
    onChange: (option) => dispatch([setInFollowUpsState("filterCompanies", option)]),
    options,
  });
}

function createFilterResponsibleModel(
  dispatch: EventDispatcher,
  value: Responsible,
  responsibles: Responsible[],
) {
  const options = responsibles.reduce<Responsible[]>(
    (acc, { id, name, email }) => {
      if (!acc.some((r) => r.id === id)) {
        acc.push({ id, name, email });
      }
      return acc;
    },
    [allResponsibles],
  );

  return new RadioFieldModel({
    value: value,
    labelFn: ({ id, name }) =>
      id === allResponsibles.id ? { key: "responsibles-all" } : { translated: name },
    idFn: ({ id }) => id,
    onChange: (option) => dispatch([setInFollowUpsState("filterResponsible", option)]),
    options,
  });
}

const prepDecisions = (state: FollowUpsState, dispatch: EventDispatcher) =>
  filterDecisionsList(state).map((d) => ({
    ...d,
    responsibleModel: createResponsibleModel(dispatch, state, d.id, d.responsibleId),
  }));

export const prepFollowUpsProps = (
  state: FollowUpsState,
  dispatch: EventDispatcher,
  isAltinnSubmission: boolean,
  user: User | null,
): FollowUpsViewProps => {
  const { activeTab, filterText, decisions, filterCompanies, filterResponsible, responsibles } =
    state;
  return {
    decisions: prepDecisions(state, dispatch),
    activeTab,
    tabs: prepTabs(decisions, activeTab),
    onSelectDecision: (decision) => dispatch([navigateFollowUps(decision.id)]),
    selectedDecision: prepSelectedDecisionProps(state, dispatch, isAltinnSubmission, user),
    filterCompaniesModel: createFilterCompaniesModel(dispatch, filterCompanies, decisions),
    filterResponsibleModel: createFilterResponsibleModel(dispatch, filterResponsible, responsibles),
    filterText,
    updateFilterText: (s) => dispatch([setInFollowUpsState("filterText", s)]),
  };
};

const navigateFollowUps = (decisionId?: string): DCEvent =>
  decisionId !== undefined
    ? navigate(`/super-admin-backoffice/followups/${decisionId}`)
    : navigate("/super-admin-backoffice/followups");

export const changeResponsible = (decisionIds: string[], responsible?: Responsible): DCEvent[] => [
  followUpsApi.updateResponsible({
    decisionIds,
    responsible,
    onSuccess: () => [
      showGeneralSuccess(),
      updateInFollowUpsState("decisions", (cur) => {
        decisionIds.map((decisionId) => {
          const d = cur.find((d) => d.id === decisionId);
          if (d !== undefined) {
            d.responsibleId = responsible?.id;
          }
        });
        return cur;
      }),
      updateInFollowUpsState("selectedDecision", (cur) => {
        if (cur !== undefined && decisionIds.includes(cur.decisionId)) {
          cur.responsibleId = responsible?.id;
        }
        return cur;
      }),
      updateInFollowUpsState("projectDecisions", (cur) => {
        cur?.map((decision) => {
          if (decisionIds.includes(decision.decisionId)) {
            decision.responsibleId = responsible?.id;
          }
        });
        return cur;
      }),
    ],
    onFailure: () => [showGeneralError()],
  }),
];

export const altinnFilename = (filename: string): string =>
  filename.normalize("NFC").replace(/[^0-9A-Za-zÀ-ÖØ-öø-ÿ ._-]/g, "");

const toggleDocumentPreview = (details?: PendingDecisionDetails, documentId?: string): DCEvent[] =>
  compact([
    details &&
      setInFollowUpsState(
        "previewDocument",
        isBlank(documentId)
          ? undefined
          : {
              filename: altinnFilename(
                details.documents.find((d) => d.documentId === documentId)!.title +
                  `_${details.company.companyName}_${formatDate(Language.NO, new Date())}.pdf`,
              ),
              file: { url: `/api/superadmin/preview-document/${documentId}` },
            },
      ),
  ]);

const showUpdateBRStatusEmailDialog = (
  decision: PendingDecisionDetails,
  projectDecisions: PendingProjectDecision[],
  selectedCompany?: CompanyItem,
): DCEvent[] => {
  const company = selectedCompany ?? decision.company;
  const companyDecisions = getProjectDecisionsByCompany(projectDecisions, company);
  const decisions = getProjectDecisionsForAltinnSubmission(companyDecisions);
  const inSubmission = getSubmissionDecisions(companyDecisions, decision.decisionId);
  return [
    {
      type: "show-dialog",
      dialog: {
        type: "decision-BR-status-update",
        props: {
          decisionId:
            company.organisationNumber === decision.company.organisationNumber
              ? decision.decisionId
              : undefined,
          decisions: new OptionFieldModel({
            options: decisions,
            idFn: (item) => item.decisionId,
            labelFn: (item) => ({
              key: "followup.decision.notify.dialog.decision.label",
              params: {
                decisionName: decisionNameWithReference(item),
                status: brStatusProps[item.status as BR_STATUS].label,
              },
            }),
            disabledFn: (item) => item.decisionId === decision.decisionId,
            selectedIds: inSubmission.map((item) => item.decisionId),
            required: true,
          }),
          body: new StringFieldModel({ required: true }),
        },
      },
    },
  ];
};

export const sendUpdateBRStatusEmail = (
  decisions: PendingProjectDecision[],
  emailNotificationText: string,
  onSuccess: DCEvent,
  decisionId?: string,
): DCEvent[] => [
  followUpsApi.sendBRStatusUpdateEmail({
    decisionIds: decisions.map((d) => d.decisionId),
    emailTextMarkdown: emailNotificationText,
    onSuccess: () =>
      compact([onSuccess, showGeneralSuccess(), decisionId && selectDecision(decisionId)]),
    onFailure: () => [showGeneralError()],
  }),
];

const toggleAttachmentPreview = (
  details?: PendingDecisionDetails,
  attachmentId?: string,
): DCEvent[] =>
  compact([
    details &&
      setInFollowUpsState(
        "previewDocument",
        isBlank(attachmentId)
          ? undefined
          : {
              filename: altinnFilename(
                details.attachments.find((a) => a.id === attachmentId)!.filename,
              ),
              file: { url: `/api/superadmin/preview-attachment/${attachmentId}` },
            },
      ),
  ]);

const toggleDecisionPreview = (decision: {
  decisionType: DecisionType;
  changedData: string;
}): DCEvent[] => [
  setInFollowUpsState("previewDocument", {
    filename: decision.decisionType + ".json",
    file: { data: new Blob([new TextEncoder().encode(decision.changedData)]) },
  }),
];

const showUpdateDecisionStatusDialog = (
  decisionIds: string[],
  status: BR_STATUS,
  model: LocalDateFieldModel,
): DCEvent[] => [
  {
    type: "show-dialog",
    dialog: { type: "decision-approval-date", decisionIds, status, model },
  },
];

const showCreateSubmissionReferenceDialog = (
  decision: PendingDecisionDetails,
  projectDecisionsByCompany: PendingProjectDecision[],
): DCEvent[] => {
  const decisions = getProjectDecisionsForAltinnSubmission(projectDecisionsByCompany);
  return [
    {
      type: "show-dialog",
      dialog: {
        type: "create-submission-reference",
        props: {
          decisionId: decision.decisionId,
          decisions: new OptionFieldModel({
            options: decisions,
            idFn: (item) => item.decisionId,
            labelFn: decisionNameWithReference,
            disabledFn: (item) =>
              item.decisionType === decision.decisionType || item.submissionReference !== undefined,
            selectedIds: decisions
              .filter((item) => item.submissionReference === undefined)
              .map((item) => item.decisionId),
            required: true,
          }),
        },
      },
    },
  ];
};

export const createSubmissionReference = (
  decisionId: string,
  decisions: PendingProjectDecision[],
  onSuccess?: DCEvent,
): DCEvent[] => [
  followUpsApi.createSubmissionReference({
    decisionIds: decisions.map((d) => d.decisionId),
    onSuccess: (response) =>
      compact([onSuccess, copyToClipboard(response.notificationEmail), selectDecision(decisionId)]),
    onFailure: () => [showGeneralError()],
  }),
];

export const updateDecisionStatus = (
  decisionIds: string[],
  user: User | null,
  status?: BR_STATUS,
  brPublishingDate?: string,
  onSuccess?: DCEvent,
): DCEvent[] => [
  followUpsApi.updateDecisionStatus({
    decisionIds,
    status,
    brPublishingDate: brPublishingDate ? combineDateAndTime(brPublishingDate, "12:00") : undefined,
    onSuccess: () =>
      compact([
        onSuccess,
        showGeneralSuccess(),
        status &&
          updateInFollowUpsState("selectedDecision", (cur) => {
            if (cur !== undefined && decisionIds.includes(cur.decisionId)) {
              cur.status = status;
              if (status === "BR_APPROVED") {
                cur.brPublishingDate = brPublishingDate ?? toLocalDateISOString(Date.now());
              } else if (status === "SENT_FOR_SIGNING" && cur.sentForSigningTime === undefined) {
                cur.sentForSigningTime = toDate(now()).toISOString();
                cur.sentForSigningBy = getFullName(user);
              }
            }
            return cur;
          }),
        status &&
          updateInFollowUpsState("projectDecisions", (cur) => {
            cur
              ?.filter((a) => decisionIds.includes(a.decisionId))
              .forEach((decision) => (decision.status = status));
            return cur;
          }),
        !status && navigateFollowUps(),
      ]),
    onFailure: () => [showGeneralError()],
  }),
];

const showSignerReminderDialog = (
  decision: PendingDecisionDetails,
  projectDecisions: PendingProjectDecision[],
): DCEvent[] => {
  const companyDecisions = getProjectDecisionsByCompany(projectDecisions, decision.company);
  const decisions = getProjectDecisionsForAltinnSubmission(companyDecisions);
  const inSubmission = getSubmissionDecisions(companyDecisions, decision.decisionId);
  return [
    {
      type: "show-dialog",
      dialog: {
        type: "decision-signer-reminder",
        props: {
          decisionId: decision.decisionId,
          decisions: new OptionFieldModel({
            options: decisions,
            idFn: (item) => item.decisionId,
            labelFn: (item) =>
              item.isRegistration
                ? {
                    key: "followup.decision.registration",
                    params: { decision: decisionNameWithReference(item) },
                  }
                : decisionNameWithReference(item),
            disabledFn: (item) => item.decisionId === decision.decisionId,
            selectedIds: inSubmission.map((item) => item.decisionId),
            required: true,
          }),
          signers: new OptionFieldModel({
            options: getAllAltinnSubmissionSigners(
              getDecisionsAwaitingSigning(projectDecisions, decision.company),
            ),
            idFn: (item) => item.id,
            labelFn: (item) => ({
              key: "followup.decision.altinn.signer.label",
              params: { description: item.description, name: item.name, email: item.email },
            }),
            required: true,
          }),
          subject: BRSignerReminderSubject(decision.company.companyName),
          body: BRSignerReminderBody(),
        },
      },
    },
  ];
};

export const sendSignerReminder = (
  decisionId: string,
  decisions: PendingProjectDecision[],
  signers: AltinnSubmissionSigner[],
  subject: string,
  body: string,
  onSuccess: DCEvent,
): DCEvent[] => [
  followUpsApi.sendBRSignerReminderEmail({
    decisionIds: decisions.map((d) => d.decisionId),
    signers: signers.map((a) => ({ name: a.name, email: a.email! })),
    subject,
    body,
    onSuccess: () => compact([onSuccess, showGeneralSuccess(), selectDecision(decisionId)]),
    onFailure: () => [showGeneralError()],
  }),
];

function createResponsibleModel(
  dispatch: EventDispatcher,
  state: FollowUpsState,
  decisionId: string,
  responsibleId?: string,
) {
  return new RadioFieldModel({
    value: state.responsibles.find((r) => r.id === responsibleId) || unassigned,
    labelFn: (option) => ({ translated: option?.name ?? "" }),
    idFn: (option) => option?.id ?? "",
    options: [...state.responsibles, unassigned],
    onChange: (option) =>
      dispatch(changeResponsible([decisionId], option.id === unassigned.id ? undefined : option)),
  });
}

const prepSelectedDecisionProps = (
  state: FollowUpsState,
  dispatch: EventDispatcher,
  isAltinnSubmission: boolean,
  user: User | null,
): DecisionDetailViewProps | undefined => {
  const decision = state.selectedDecision;
  const projectDecisions = state.projectDecisions;
  if (!decision || !projectDecisions) {
    return undefined;
  }
  const projectDecisionsByCompany = getProjectDecisionsByCompany(
    projectDecisions,
    state.selectedCompany,
  );
  // Only the Altinn view pages through companies; elsewhere the decision's own company applies.
  const submissionDecisions = getSubmissionDecisions(
    isAltinnSubmission
      ? projectDecisionsByCompany
      : getProjectDecisionsByCompany(projectDecisions, decision.company),
    decision.decisionId,
  );
  return {
    decision,
    projectDecisions,
    submissionDecisions,
    onGenerateNotificationEmail: () => {
      const decisions = getProjectDecisionsForAltinnSubmission(projectDecisionsByCompany);
      dispatch(
        decisions.length === 1
          ? createSubmissionReference(decision.decisionId, decisions)
          : showCreateSubmissionReferenceDialog(decision, projectDecisionsByCompany),
      );
    },
    onMarkSubmissionSentForSigning: () =>
      dispatch(
        updateDecisionStatus(
          submissionDecisions.filter((d) => d.status === "SENT_TO_BR").map((d) => d.decisionId),
          user,
          "SENT_FOR_SIGNING",
        ),
      ),
    onMarkDecisionSentForSigning: (decisionId) =>
      dispatch(updateDecisionStatus([decisionId], user, "SENT_FOR_SIGNING")),
    documents: getCompanyDocuments(
      decision,
      isAltinnSubmission
        ? state.selectedCompany?.organisationNumber
        : decision.company.organisationNumber,
    ),
    projectDecisionsByCompany,
    companies: getProjectDecisionCompaniesSelectorProps(
      dispatch,
      projectDecisions,
      state.selectedCompany,
    ),
    showAltinnSubmission: isAltinnSubmission,
    previewDocument: state.previewDocument,
    isRegisteredOnDcompany: decision.decisionStatus !== DecisionStatus.COMPLETED,
    responsibles: state.responsibles,
    assignedToModel: createResponsibleModel(
      dispatch,
      state,
      decision.decisionId,
      decision.responsibleId,
    ),
    onChangeResponsible: (decisionIds: string[], responsible) =>
      dispatch(changeResponsible(decisionIds, responsible)),
    goToAltinnSubmission: () =>
      dispatch([navigate(`/super-admin-backoffice/followups/${decision.decisionId}/altinn`)]),
    onCloseAltinnSubmission: () => dispatch([navigateFollowUps(decision.decisionId)]),
    onBack: () => dispatch([navigateFollowUps()]),
    onUpdateStatus: (dateModel, status) => {
      const decisionIds = submissionDecisions.map((d) => d.decisionId);
      if (status === "BR_APPROVED") {
        dispatch(showUpdateDecisionStatusDialog(decisionIds, status, dateModel));
      } else {
        dispatch(updateDecisionStatus(decisionIds, user, status));
      }
    },
    openSignerReminderDialog: () => dispatch(showSignerReminderDialog(decision, projectDecisions)),
    openAddToSubmissionReferenceDialog: () =>
      dispatch([showAddToSubmissionReferenceDialog(decision)]),
    onResetSubmission: () =>
      dispatch(
        confirmAction({
          title: { key: "followup.decision.reset.confirm.title" },
          text: {
            key: "followup.decision.reset.confirm.text",
            params: { count: submissionDecisions.length },
          },
          confirmLabel: { key: "followup.decision.reset" },
          onConfirm: updateDecisionStatus(
            submissionDecisions.map((d) => d.decisionId),
            user,
          ),
        }),
      ),
    openUpdateBRStatusDialog: (selectedCompany) =>
      dispatch(showUpdateBRStatusEmailDialog(decision, projectDecisions, selectedCompany)),
    onToggleDocumentPreview: (documentIdOrEvent) => {
      dispatch(
        toggleDocumentPreview(
          state.selectedDecision,
          state.previewDocument ? undefined : documentIdOrEvent,
        ),
      );
    },
    onToggleAttachmentPreview: (attachmentIdOrEvent) =>
      dispatch(
        toggleAttachmentPreview(
          state.selectedDecision,
          state.previewDocument ? undefined : attachmentIdOrEvent,
        ),
      ),
    onToggleDecisionPreview: toggleDecisionPreview,
    brPublishingDateModel: new LocalDateFieldModel({
      value: toLocalDateISOString(decision.brPublishingDate ?? Date.now()),
    }),
  };
};

export const brStatusUpdateEmailTypes = ["SENT_FOR_SIGNING", "PROCESSING", "BR_APPROVED"] as const;
type BRStatusUpdateEmailType = (typeof brStatusUpdateEmailTypes)[number];

export const mapBRStatusToEmailNotificationText: {
  [key in BRStatusUpdateEmailType]: { label: TMessage; body: string };
} = {
  SENT_FOR_SIGNING: {
    label: { key: "followup.decision.notify.dialog.email.type.created" },
    body: `Samordnet registermelding er opprettet og venter nå på signering i Altinn. Du vil motta en oppdatering så snart meldingen er signert og sendt til Brønnøysundregistrene for behandling.

Mvh. oss i dCompany
    `,
  },
  PROCESSING: {
    label: { key: "followup.decision.notify.dialog.email.type.signed" },
    body: `Meldingen er ferdig signert i Altinn og sendt til Brønnøysundregistrene for behandling.

Tips. Saksbehandlingskøen i Brønnøysundregistrene oppdateres hver time:
https://www.brreg.no/hvordan-kan-vi-hjelpe-deg/nar-blir-saken-min-behandlet/

Mvh oss i dCompany
    `,
  },
  BR_APPROVED: {
    label: { key: "followup.decision.notify.dialog.email.type.approved" },
    body: `Meldingen har blitt godkjent av Brønnøysundregistrene

Mvh oss i dCompany
    `,
  },
} as const;

const BRSignerReminderSubject = (companyName: string): StringFieldModel => {
  const text = `🔔 Påminnelse: ${companyName} - En registermelding venter på din signatur i Altinn`;
  return new StringFieldModel({ value: text });
};

const BRSignerReminderBody = (): StringFieldModel => {
  const text = `**En samordnet registermelding venter fortsatt på din signatur i Altinn.**

Logg inn i Altinn for å fullføre signeringen.

Vennlig hilsen,
dCompany`;
  return new StringFieldModel({ value: text });
};

const selectDecision = (
  decisionId: string,
  onSuccess?: (response: GetPendingDecisionDetailsResponse) => DCEvent[],
): DCEvent =>
  followUpsApi.getPendingDecisionDetails({
    decisionId: decisionId,
    onSuccess: (response) =>
      compact([
        ...(onSuccess?.(response) ?? []),
        setInFollowUpsState("selectedDecision", response.decision),
        setInFollowUpsState("projectDecisions", response.projectDecisions),
        setInFollowUpsState("responsibles", response.responsibles),
        setInFollowUpsState("isLoading", false),
      ]),
    onFailure: () => [showGeneralError()],
  });

export const filterDecisionsList = (state: FollowUpsState) => {
  const { decisions, filterCompanies, filterResponsible, filterText } = state;
  return decisions.filter((d) => {
    const lowerCased = filterText.toLowerCase();
    const textFilter =
      isBlank(filterText) ||
      d.companyName.toLowerCase().includes(lowerCased) ||
      d.orgNo.includes(lowerCased);

    const companyFilter =
      filterCompanies.organisationNumber === allCompanies.organisationNumber ||
      filterCompanies.organisationNumber === d.orgNo;

    const responsibleFilter =
      filterResponsible.id === allResponsibles.id || filterResponsible.id === d.responsibleId;

    return textFilter && companyFilter && responsibleFilter;
  });
};

const countForTab = (tab: FollowUpTab, decisions: PendingDecisionListItem[]): number =>
  countSubmissions(filterPendingDecisionsByTab(decisions, tab));

export const countSubmissions = (decisions: PendingDecisionListItem[]): number =>
  uniqBy(decisions, (d) => d.submissionReference ?? d.id).length;

export const cleanUpFollowUpsPage = (): DCEvent[] => [
  { type: "update-store", data: { followUps: undefined } },
];

export const defaultFollowUpsState = (): FollowUpsState => ({
  isLoading: true,
  decisions: [],
  activeTab: "new",
  responsibles: [],
  filterText: "",
  filterCompanies: allCompanies,
  filterResponsible: allResponsibles,
});

export const setInFollowUpsState = <K extends keyof FollowUpsState>(
  key: K,
  val: FollowUpsState[K],
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    followUps: (followUps = defaultFollowUpsState()) => ({ ...followUps, [key]: val }),
  },
});

const updateInFollowUpsState = <K extends keyof FollowUpsState>(
  key: K,
  val: (cur: FollowUpsState[K]) => FollowUpsState[K],
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    followUps: (followUpsState = defaultFollowUpsState()) => ({
      ...followUpsState,
      [key]: val(followUpsState[key]),
    }),
  },
});

export type FollowUpsState = {
  isLoading: boolean;
  decisions: PendingDecisionListItem[];
  filterText: string;
  filterCompanies: CompanyItem;
  filterResponsible: Responsible;
  responsibles: Responsible[];
  activeTab: FollowUpTab;
  selectedCompany?: CompanyItem;
  selectedDecision?: PendingDecisionDetails;
  projectDecisions?: PendingProjectDecision[];
  previewDocument?: FilePreviewProps;
};

const followUpsApi = {
  getPendingDecisions: ({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (pendingDecisionsResponse: GetPendingDecisionsResponse) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `superadmin/pending-decisions`,
      onSuccess: (r) => onSuccess(r.data),
      onFailure,
    }),

  getPendingDecisionDetails: ({
    decisionId,
    onSuccess,
    onFailure,
  }: {
    decisionId: string;
    onSuccess: (response: GetPendingDecisionDetailsResponse) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: `superadmin/pending-decision/${decisionId}`,
      onSuccess: (r) => onSuccess(r.data),
      onFailure,
    }),

  updateResponsible: ({
    decisionIds,
    responsible,
    onSuccess,
    onFailure,
  }: {
    decisionIds: string[];
    responsible?: Responsible;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/update-responsible`,
      data: { decisionIds, responsibleId: responsible?.id },
      onSuccess,
      onFailure,
    }),

  updateDecisionStatus: ({
    decisionIds,
    status,
    brPublishingDate,
    onSuccess,
    onFailure,
  }: {
    decisionIds: string[];
    status?: BR_STATUS;
    brPublishingDate?: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/update-decision-status`,
      data: {
        decisionIds: decisionIds,
        status: status,
        brPublishingDate: status === "BR_APPROVED" ? brPublishingDate : undefined,
      },
      onSuccess,
      onFailure,
    }),

  addToSubmissionReference: ({
    reference,
    decisionId,
    onSuccess,
    onFailure,
  }: {
    reference: number;
    decisionId: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/br-submission-reference/${reference}/decisions`,
      data: { decisionId },
      onSuccess,
      onFailure,
    }),

  createSubmissionReference: ({
    decisionIds,
    onSuccess,
    onFailure,
  }: {
    decisionIds: string[];
    onSuccess: (response: CreateSubmissionReferenceResponse) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/br-submission-reference`,
      data: { decisionIds },
      onSuccess: (r) => onSuccess(r.data),
      onFailure,
    }),

  sendBRStatusUpdateEmail: ({
    decisionIds,
    emailTextMarkdown,
    onSuccess,
    onFailure,
  }: {
    decisionIds: string[];
    emailTextMarkdown: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/decision-send-br-status-email`,
      data: { decisionIds, emailTextMarkdown },
      onSuccess,
      onFailure,
    }),

  sendBRSignerReminderEmail: ({
    decisionIds,
    signers,
    subject,
    body,
    onSuccess,
    onFailure,
  }: {
    decisionIds: string[];
    signers: AltinnSigner[];
    subject: string;
    body: string;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: `superadmin/decision-send-br-signer-reminder-email`,
      data: { decisionIds, signers, subject, body },
      onSuccess,
      onFailure,
    }),
};

export const filterPendingDecisionsByTab = <T extends PendingDecisionListItem>(
  decisions: T[],
  activeTab: FollowUpTab,
): T[] => {
  switch (activeTab) {
    case "new":
      return decisions.filter((d) => d.status === "SENT_TO_BR");
    case "deleted":
      return decisions.filter((d) => d.status === "DELETED");
    case "sent-for-signing":
      return decisions.filter((d) => d.status === "SENT_FOR_SIGNING");
    case "processing":
      return decisions.filter((d) => d.status === "PROCESSING");
    case "approved":
      return decisions.filter((d) => d.status === "BR_APPROVED");
    case "rejected":
      return decisions.filter((d) => d.status === "REJECTED");
    case "needs-documentation":
      return decisions.filter((d) => d.status === "NEEDS_DOCUMENTATION");
  }
};

export const followUpTabDescription = (activeTab: FollowUpTab): TMessage | undefined => {
  switch (activeTab) {
    case "new":
      return { key: "followup.tabs.new.description" };
    case "sent-for-signing":
      return { key: "followup.tabs.sent-for-signing.description" };
    case "processing":
      return { key: "followup.tabs.processing.description" };
    default:
      return undefined;
  }
};

export const showDecisionFollowUpWarning = (
  sentToBRTime: string,
  activeTab: FollowUpTab,
): boolean => {
  switch (activeTab) {
    case "new":
      return diffDays(sentToBRTime, Date.now()) > 1;
    case "sent-for-signing":
      return diffDays(sentToBRTime, Date.now()) > 7;
    case "processing":
      return diffDays(sentToBRTime, Date.now()) > 21;
    default:
      return false;
  }
};

export const altinnDateFormat = new Intl.DateTimeFormat("no-NB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export const altinnNumberFormat = new Intl.NumberFormat("no-NB", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: false,
});

export const contributionTypeLabel: { [key in ContributionType]: TMessageKey } = {
  cash: "followup.decision.altinn.increase-share-capital.contribution-types.cash",
  debt: "followup.decision.altinn.increase-share-capital.contribution-types.debt",
  kind: "followup.decision.altinn.increase-share-capital.contribution-types.kind",
} as const;

export const brStatusProps: {
  [key in BR_STATUS]: { color: FgColor; label: TMessage; icon: IconName };
} = {
  SENT_TO_BR: {
    color: "warning",
    label: { key: "new" },
    icon: "dot-outline",
  },
  SENT_FOR_SIGNING: {
    color: "subtle",
    label: { key: "sent-for-signing" },
    icon: "paper-plane",
  },
  PROCESSING: {
    color: "info",
    label: { key: "processing" },
    icon: "arrow-clockwise",
  },
  BR_APPROVED: {
    color: "success",
    label: { key: "approved" },
    icon: "check",
  },
  REJECTED: {
    color: "error",
    label: { key: "rejected" },
    icon: "x-circle",
  },
  NEEDS_DOCUMENTATION: {
    color: "warning",
    label: { key: "needs-documentation" },
    icon: "warning-circle",
  },
  DELETED: {
    color: "error",
    label: { key: "general.deleted" },
    icon: "x",
  },
} as const;

export const brSignerRoleLabel: {
  [key in BRSignerRole]: TMessage;
} = {
  ceo: { key: "roles.ceo" },
  chairperson: { key: "roles.chairperson" },
  member: { key: "roles.member" },
  other: { key: "sendToBr.specify" },
} as const;
